import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { PromoCalculator } from "../components/PromoCalculator";
import { AIModelSelector } from "../components/AIModelSelector";
import { toast } from "sonner";
import {
  TrendingUp,
  TrendingDown,
  Calendar,
  Loader2,
  Flame,
  ArrowDownRight,
  ArrowUpRight,
  Sparkles,
  Calculator,
  Target,
  RefreshCw,
  Tag,
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;

const demandColor = (level) => {
  if (level === "muy_alta") return "#dc2626";
  if (level === "alta") return "#ea580c";
  if (level === "media") return "#ca8a04";
  if (level === "baja") return "#0891b2";
  return "#64748b";
};

const demandLabel = (level) => {
  if (level === "muy_alta") return "Muy alta";
  if (level === "alta") return "Alta";
  if (level === "media") return "Media";
  if (level === "baja") return "Baja";
  return "Muy baja";
};

const Forecast = () => {
  const [forecastDays, setForecastDays] = useState(14);
  const [forecast, setForecast] = useState(null);
  const [loadingForecast, setLoadingForecast] = useState(true);

  const [priceChange, setPriceChange] = useState([0]);
  const [simDays, setSimDays] = useState([14]);
  const [simulation, setSimulation] = useState(null);
  const [loadingSim, setLoadingSim] = useState(false);

  // Revenue goal + AI suggestions
  const [goalData, setGoalData] = useState(null);
  const goalLabel = goalData ? `€${(goalData.production.goal / 1_000_000).toFixed(1)}M` : "el objetivo";

  const [suggestions, setSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [volumeMode, setVolumeMode] = useState(true);
  const [aiModel, setAiModel] = useState(() => localStorage.getItem("ai_model") || "claude");
  const changeAiModel = (m) => { setAiModel(m); localStorage.setItem("ai_model", m); };

  const getAuthHeaders = useCallback(
    () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` }),
    []
  );

  const fetchForecast = useCallback(async (days) => {
    setLoadingForecast(true);
    try {
      const res = await axios.get(`${API}/api/analytics/demand-forecast?days=${days}`, {
        headers: getAuthHeaders(),
      });
      setForecast(res.data);
    } catch (e) {
      toast.error("Error cargando previsión de demanda");
    } finally {
      setLoadingForecast(false);
    }
  }, [getAuthHeaders]);

  const runSimulation = useCallback(async () => {
    setLoadingSim(true);
    try {
      const res = await axios.post(
        `${API}/api/analytics/price-simulator`,
        { price_change_percent: priceChange[0], days: simDays[0] },
        { headers: getAuthHeaders() }
      );
      setSimulation(res.data);
    } catch (e) {
      toast.error("Error en simulación");
    } finally {
      setLoadingSim(false);
    }
  }, [priceChange, simDays, getAuthHeaders]);

  const fetchGoalAndCachedSuggestions = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [goalRes, cachedRes] = await Promise.all([
        axios.get(`${API}/api/analytics/revenue-goal`, { headers }),
        axios.get(`${API}/api/ai/revenue-suggestions/cached`, { headers }),
      ]);
      setGoalData(goalRes.data);
      if (cachedRes.data && cachedRes.data.suggestions && cachedRes.data.suggestions.length > 0) {
        setSuggestions(cachedRes.data);
      }
    } catch (e) {
      console.log("Goal/suggestions cache not available:", e.message);
    }
  }, [getAuthHeaders]);

  const generateSuggestions = useCallback(async () => {
    setLoadingSuggestions(true);
    try {
      const res = await axios.get(`${API}/api/ai/revenue-suggestions?days=14&mode=${volumeMode ? "volumen" : "normal"}&model=${aiModel}`, {
        headers: getAuthHeaders(),
        timeout: 120000,
      });
      if (res.data && res.data.success) {
        setSuggestions(res.data);
        toast.success(`${res.data.suggestions.length} sugerencias generadas`);
      } else {
        toast.error(res.data?.error || "Error generando sugerencias");
      }
    } catch (e) {
      toast.error("Error generando sugerencias IA");
    } finally {
      setLoadingSuggestions(false);
    }
  }, [getAuthHeaders, volumeMode, aiModel]);

  useEffect(() => {
    fetchForecast(forecastDays);
  }, [fetchForecast, forecastDays]);

  useEffect(() => {
    runSimulation();
    fetchGoalAndCachedSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const forecastChartData = (forecast?.forecast || []).map((f) => ({
    date: `${f.day_name} ${f.date.slice(8)}`,
    predicted: f.predicted_occupancy,
    oscar: f.oscar_occupancy,
    eventImpact: f.event_impact,
    color: demandColor(f.demand_level),
  }));

  const simChartData = (simulation?.days || []).map((d) => ({
    date: `${d.day_name} ${d.date.slice(8)}`,
    base: d.baseline_revenue,
    sim: d.simulated_revenue,
  }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="forecast-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Previsión & Simulador</h1>
          <p className="text-muted-foreground">
            Predicción de demanda y simulación de impacto en ingresos
          </p>
        </div>
      </div>

      <Tabs defaultValue="suggestions" className="space-y-4">
        <TabsList className="max-w-full justify-start overflow-x-auto" data-testid="forecast-tabs-list">
          <TabsTrigger value="suggestions" data-testid="tab-suggestions">
            <Target className="w-4 h-4 mr-2" />
            IA Acciones {goalLabel}
          </TabsTrigger>
          <TabsTrigger value="forecast" data-testid="tab-forecast">
            <Sparkles className="w-4 h-4 mr-2" />
            Previsión de Demanda
          </TabsTrigger>
          <TabsTrigger value="simulator" data-testid="tab-simulator">
            <Calculator className="w-4 h-4 mr-2" />
            Simulador de Precios
          </TabsTrigger>
          <TabsTrigger value="promos" data-testid="tab-promos">
            <Tag className="w-4 h-4 mr-2" />
            Promo 2x1
          </TabsTrigger>
        </TabsList>

        {/* === PROMO CALCULATOR TAB === */}
        <TabsContent value="promos" className="space-y-4">
          <PromoCalculator />
        </TabsContent>

        {/* === AI SUGGESTIONS TAB === */}
        <TabsContent value="suggestions" className="space-y-4">
          {goalData && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-primary" />
                    <CardTitle>Camino a €{(goalData.production.goal / 1_000_000).toFixed(1)}M</CardTitle>
                  </div>
                  <Badge
                    style={{
                      backgroundColor:
                        goalData.production.status_color === "green" ? "#10b981"
                        : goalData.production.status_color === "yellow" ? "#f59e0b" : "#ef4444",
                      color: "white",
                    }}
                  >
                    {goalData.production.status_label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-muted-foreground">Producción {goalData.current_year} (Ene-Dic): €{goalData.production.projected_full_year.toLocaleString("es-ES")}</span>
                      <span className="font-bold">{((goalData.production.projected_full_year / goalData.production.goal) * 100).toFixed(1)}%</span>
                    </div>
                    <div className="h-3 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${Math.min(100, (goalData.production.projected_full_year / goalData.production.goal) * 100)}%`,
                          backgroundColor: goalData.production.status_color === "green" ? "#10b981"
                            : goalData.production.status_color === "yellow" ? "#f59e0b" : "#ef4444",
                        }}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div className="bg-muted/30 rounded-lg p-3 text-center">
                      <p className="text-xs text-muted-foreground">Brecha</p>
                      <p className="font-bold text-red-600">€{goalData.production.gap_to_goal.toLocaleString("es-ES")}</p>
                    </div>
                    <div className="bg-muted/30 rounded-lg p-3 text-center">
                      <p className="text-xs text-muted-foreground">Necesario/día</p>
                      <p className="font-bold">€{goalData.production.daily_needed_to_reach_goal.toLocaleString("es-ES")}</p>
                    </div>
                    <div className="bg-muted/30 rounded-lg p-3 text-center">
                      <p className="text-xs text-muted-foreground">Ritmo actual/día</p>
                      <p className={`font-bold ${
                        goalData.production.daily_pace_current >= goalData.production.daily_needed_to_reach_goal
                          ? "text-green-600" : "text-orange-600"
                      }`}>
                        €{goalData.production.daily_pace_current.toLocaleString("es-ES")}
                      </p>
                    </div>
                    <div className="bg-muted/30 rounded-lg p-3 text-center">
                      <p className="text-xs text-muted-foreground">Días restantes</p>
                      <p className="font-bold">{goalData.production.days_left}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle className="text-lg sm:text-xl">Sugerencias IA para alcanzar {goalLabel}</CardTitle>
                  <CardDescription>
                    Acciones generadas por {suggestions?.model?.label || (aiModel === "chatgpt" ? "ChatGPT" : "Claude")} basadas en tus datos reales
                  </CardDescription>
                  <label className="mt-3 flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="volume-mode-label">
                    <Switch checked={volumeMode} onCheckedChange={setVolumeMode} data-testid="volume-mode-switch" />
                    <span className={volumeMode ? "font-semibold text-foreground" : "text-muted-foreground"}>
                      Sin demanda: no sugerir subidas de precio (promos 2x1 / 3x2)
                    </span>
                  </label>
                  <div className="mt-3 flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Modelo:</span>
                    <AIModelSelector value={aiModel} onChange={changeAiModel} testIdPrefix="suggestions-model" />
                    {suggestions?.model?.label && (
                      <span className="text-xs text-muted-foreground" data-testid="suggestions-generated-by">generado con {suggestions.model.label}</span>
                    )}
                  </div>
                </div>
                <Button onClick={generateSuggestions} disabled={loadingSuggestions} data-testid="generate-suggestions-btn" className="w-full sm:w-auto">
                  {loadingSuggestions ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generando...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {suggestions ? "Actualizar" : "Generar"} sugerencias
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!suggestions && !loadingSuggestions && (
                <div className="text-center py-12 text-muted-foreground">
                  <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Pulsa "Generar sugerencias" para que la IA analice tus datos</p>
                  <p className="text-xs mt-1">y proponga 3 acciones concretas para llegar a {goalLabel}</p>
                </div>
              )}

              {suggestions && (
                <div className="space-y-4">
                  {suggestions.summary && (
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                      <p className="text-sm text-blue-900" data-testid="ai-summary">
                        <strong>Resumen IA:</strong> {suggestions.summary}
                      </p>
                    </div>
                  )}

                  <div className="space-y-3">
                    {(suggestions.suggestions || []).map((s, idx) => {
                      const priorityColor = s.priority === "alta" ? "#dc2626"
                        : s.priority === "media" ? "#f59e0b" : "#0891b2";
                      const catBg = s.category === "subir_precio" ? "bg-green-50 border-green-200"
                        : s.category === "bajar_precio" ? "bg-orange-50 border-orange-200"
                        : ["promocion", "2x1", "3x2", "larga_estancia"].includes(s.category) ? "bg-purple-50 border-purple-200"
                        : s.category === "mice" ? "bg-blue-50 border-blue-200"
                        : s.category === "canal" ? "bg-cyan-50 border-cyan-200"
                        : "bg-slate-50 border-slate-200";
                      return (
                        <Card key={`suggestion-${idx}`} className={`border-2 ${catBg}`} data-testid={`suggestion-card-${idx}`}>
                          <CardContent className="pt-4">
                            <div className="flex items-start justify-between mb-2 gap-2">
                              <h4 className="font-bold text-base">{s.title}</h4>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                {s.category && (
                                  <Badge variant="outline" className="text-[10px] uppercase" data-testid={`suggestion-category-${idx}`}>
                                    {String(s.category).replace("_", " ")}
                                  </Badge>
                                )}
                                <Badge style={{ backgroundColor: priorityColor, color: "white" }}>
                                  {s.priority}
                                </Badge>
                              </div>
                            </div>
                            <p className="text-sm mb-3">{s.action}</p>
                            {s.rationale && (
                              <p className="text-xs text-muted-foreground italic mb-3">
                                <strong>Por qué:</strong> {s.rationale}
                              </p>
                            )}
                            <div className="flex flex-wrap items-center gap-3 text-sm">
                              <div className="flex items-center gap-1 font-bold text-green-700">
                                <ArrowUpRight className="w-4 h-4" />
                                +€{Number(s.estimated_impact_eur || 0).toLocaleString("es-ES")}
                              </div>
                              {s.target_dates && s.target_dates.length > 0 && (
                                <div className="text-xs text-muted-foreground">
                                  📅 {s.target_dates.slice(0, 3).join(", ")}
                                  {s.target_dates.length > 3 ? ` +${s.target_dates.length - 3}` : ""}
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>

                  {suggestions.total_estimated_impact_eur > 0 && (
                    <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 text-center">
                      <p className="text-sm text-muted-foreground">Impacto total estimado de las {suggestions.suggestions?.length || 0} acciones</p>
                      <p className="text-3xl font-bold text-green-700 mt-1" data-testid="total-impact">
                        +€{Number(suggestions.total_estimated_impact_eur).toLocaleString("es-ES")}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === FORECAST TAB === */}
        <TabsContent value="forecast" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Previsión de Demanda</CardTitle>
                  <CardDescription>
                    Próximos {forecastDays} días · {forecast?.events_in_range || 0} eventos en rango
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  {[7, 14, 30].map((d) => (
                    <Button
                      key={d}
                      variant={forecastDays === d ? "default" : "outline"}
                      size="sm"
                      onClick={() => setForecastDays(d)}
                      data-testid={`forecast-days-${d}`}
                    >
                      {d} días
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingForecast ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={forecastChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={70} />
                      <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} unit="%" />
                      <Tooltip
                        formatter={(value, name) => {
                          if (name === "predicted") return [`${value}%`, "Predicción"];
                          if (name === "oscar") return [`${value}%`, "Oscar (real)"];
                          return [value, name];
                        }}
                      />
                      <Legend />
                      <Bar dataKey="predicted" name="Predicción">
                        {forecastChartData.map((entry) => (
                          <Cell key={entry.date} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>

                  <div className="mt-6 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="text-left p-2">Día</th>
                          <th className="text-right p-2">Predicción</th>
                          <th className="text-right p-2">Oscar real</th>
                          <th className="text-center p-2">Demanda</th>
                          <th className="text-left p-2">Eventos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(forecast?.forecast || []).map((f) => (
                          <tr key={f.date} className="border-b hover:bg-muted/30" data-testid={`forecast-row-${f.date}`}>
                            <td className="p-2 font-medium">
                              {f.day_name} {f.date.slice(8, 10)}/{f.date.slice(5, 7)}
                            </td>
                            <td className="text-right p-2 font-mono">{f.predicted_occupancy}%</td>
                            <td className="text-right p-2 font-mono text-muted-foreground">
                              {f.oscar_occupancy !== null ? `${f.oscar_occupancy}%` : "—"}
                            </td>
                            <td className="text-center p-2">
                              <Badge style={{ backgroundColor: demandColor(f.demand_level), color: "white" }}>
                                {demandLabel(f.demand_level)}
                              </Badge>
                            </td>
                            <td className="p-2 text-xs text-muted-foreground">
                              {f.events.length > 0 ? f.events.join(", ") : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === SIMULATOR TAB === */}
        <TabsContent value="simulator" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Simulador de Precios</CardTitle>
              <CardDescription>
                Estima impacto en ingresos y ocupación al modificar precios
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Cambio de precio</span>
                    <Badge variant={priceChange[0] >= 0 ? "default" : "secondary"} data-testid="price-change-badge">
                      {priceChange[0] >= 0 ? "+" : ""}{priceChange[0]}%
                    </Badge>
                  </div>
                  <Slider
                    value={priceChange}
                    onValueChange={setPriceChange}
                    min={-30}
                    max={30}
                    step={1}
                    className="my-4"
                    data-testid="price-change-slider"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>-30%</span>
                    <span>0%</span>
                    <span>+30%</span>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Período</span>
                    <Badge>{simDays[0]} días</Badge>
                  </div>
                  <Slider
                    value={simDays}
                    onValueChange={setSimDays}
                    min={7}
                    max={60}
                    step={1}
                    className="my-4"
                    data-testid="sim-days-slider"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>7 días</span>
                    <span>30 días</span>
                    <span>60 días</span>
                  </div>
                </div>
              </div>

              <Button onClick={runSimulation} disabled={loadingSim} className="w-full" data-testid="run-simulation-btn">
                {loadingSim ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Calculator className="w-4 h-4 mr-2" />
                )}
                Simular escenario
              </Button>

              {simulation && simulation.success && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <p className="text-xs text-muted-foreground mb-1">Precio medio actual</p>
                        <p className="text-2xl font-bold" data-testid="sim-current-price">
                          €{simulation.current.avg_price}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <p className="text-xs text-muted-foreground mb-1">Precio simulado</p>
                        <p className="text-2xl font-bold" data-testid="sim-new-price">
                          €{simulation.simulated.avg_price}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <p className="text-xs text-muted-foreground mb-1">Ingresos baseline</p>
                        <p className="text-2xl font-bold">
                          €{simulation.totals.baseline_revenue.toLocaleString("es-ES")}
                        </p>
                      </CardContent>
                    </Card>
                    <Card
                      className={`border-2 ${
                        simulation.totals.revenue_diff >= 0 ? "border-green-500" : "border-red-500"
                      }`}
                    >
                      <CardContent className="pt-4 text-center">
                        <p className="text-xs text-muted-foreground mb-1">Ingresos simulados</p>
                        <p
                          className={`text-2xl font-bold ${
                            simulation.totals.revenue_diff >= 0 ? "text-green-600" : "text-red-600"
                          }`}
                          data-testid="sim-revenue-result"
                        >
                          €{simulation.totals.simulated_revenue.toLocaleString("es-ES")}
                        </p>
                        <div className={`flex items-center justify-center gap-1 mt-1 text-sm font-medium ${
                          simulation.totals.revenue_diff >= 0 ? "text-green-600" : "text-red-600"
                        }`}>
                          {simulation.totals.revenue_diff >= 0 ? (
                            <ArrowUpRight className="w-4 h-4" />
                          ) : (
                            <ArrowDownRight className="w-4 h-4" />
                          )}
                          {simulation.totals.revenue_diff >= 0 ? "+" : ""}
                          {simulation.totals.revenue_diff_percent}%
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <Card
                    className={
                      simulation.recommendation_level === "positive"
                        ? "bg-green-50 border-green-200"
                        : simulation.recommendation_level === "negative"
                        ? "bg-red-50 border-red-200"
                        : "bg-yellow-50 border-yellow-200"
                    }
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <Flame
                          className={`w-5 h-5 mt-0.5 ${
                            simulation.recommendation_level === "positive"
                              ? "text-green-600"
                              : simulation.recommendation_level === "negative"
                              ? "text-red-600"
                              : "text-yellow-600"
                          }`}
                        />
                        <div>
                          <p className="font-semibold">Recomendación</p>
                          <p className="text-sm mt-1" data-testid="sim-recommendation">
                            {simulation.recommendation}
                          </p>
                          <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
                            <div>
                              <span className="text-muted-foreground">Ocupación baseline:</span>{" "}
                              <span className="font-mono font-medium">
                                {simulation.totals.avg_occupancy_baseline}%
                              </span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Ocupación simulada:</span>{" "}
                              <span className="font-mono font-medium">
                                {simulation.totals.avg_occupancy_simulated}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Detalle día a día</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <AreaChart data={simChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={70} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(v) => `€${Number(v).toFixed(0)}`} />
                          <Legend />
                          <Area type="monotone" dataKey="base" stroke="#94a3b8" fill="#cbd5e1" name="Baseline" />
                          <Area type="monotone" dataKey="sim" stroke="#16a34a" fill="#86efac" name="Simulado" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Forecast;
